<template>
  <!-- Dev-only tool: rendered ONLY under the vite dev server (see agentEndpoint.ts).
       Strings are intentionally NOT i18n'd — this component is stripped from every
       deployed build, so its strings never reach a user. -->
  <div
    v-if="available"
    class="inline-flex items-center gap-2 border border-dashed border-amber-400 bg-amber-50 rounded-md px-2 py-1"
    v-tooltip.bottom="tooltip"
  >
    <i class="pi pi-wrench text-amber-500 text-xs" />
    <SelectButton
      :modelValue="override.mode"
      :options="modes"
      optionLabel="label"
      optionValue="value"
      size="small"
      :allowEmpty="false"
      @update:modelValue="(m: 'cloud' | 'local') => update({ mode: m })"
    />
    <template v-if="override.mode === 'local'">
      <InputNumber
        :modelValue="override.port"
        :useGrouping="false"
        :min="1"
        :max="65535"
        size="small"
        inputClass="!w-20 !text-xs"
        @update:modelValue="(p: number | null) => update({ port: p ?? 8000 })"
      />
      <!-- Name what LISTENS on each port — two servers — not what the browser uses
           them for; the roles are the tooltip's job. Only one of the two is typed
           in: the runner's port is derived, so the pair cannot silently disagree. -->
      <span class="text-xs text-surface-500">
        agents :{{ override.port }} · runner :{{ runnerPort }}
      </span>
    </template>
    <span v-else class="text-xs text-surface-500">→ deployed services</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import SelectButton from 'primevue/selectbutton'
import InputNumber from 'primevue/inputnumber'
import {
  isAgentEndpointSwitchAvailable,
  getAgentEndpointOverride,
  runnerPortFor,
  setAgentEndpointOverride,
  type AgentEndpointOverride,
} from '@/utils/agentEndpoint'

const available = isAgentEndpointSwitchAvailable()
const override = ref<AgentEndpointOverride>(getAgentEndpointOverride())

const modes = [
  { label: 'Cloud', value: 'cloud' },
  { label: 'Local', value: 'local' },
]

const runnerPort = computed(() => runnerPortFor(override.value.port))

const tooltip = computed(() =>
  override.value.mode === 'cloud'
    ? 'Dev only - chat and assessment actions both go to the deployed services.'
    : `Dev only. Chat → agents server on :${override.value.port}. Assessment actions ` +
      `→ the local runner on :${runnerPort.value} (derived), which admits the command ` +
      `and wakes that same agents server.\n` +
      `Start both: ./scripts/agent-dev.sh start --port ${override.value.port} --with-runner\n` +
      `Remote dev? Forward :${runnerPort.value} too - the browser calls it directly.`,
)

function update(patch: Partial<AgentEndpointOverride>) {
  override.value = { ...override.value, ...patch }
  setAgentEndpointOverride(override.value)
}
</script>
